import React, {
  Fragment,
  memo,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs';
import {
  IconEdit16,
  IconEmail16,
  IconPhone16,
  IconPlusCircle24,
  IconWhatsApp16,
} from '@app/icons';
import { useNavigate } from 'react-router-dom';
import { ModalAddEmployee, ModalReport } from '@app/modals';
import { LayoutProfile } from '@app/layouts';
import { ProfileCompanyAdditionalInfo, ProfileCompanyInfo } from './components';
import { deleteCompanyFile, uploadCompanyFiles } from '@app/api';
import { Company, TenderTypes } from '@app/models';
import { ProfileAvatar } from 'pages/Profile/components';
import AboutUser from 'pages/Profile/components/AboutUser';
import { useNotification, useUserContext } from '@app/providers';
import Reviews from '../Reviews';
import { UsersSearch } from '@app/common';
import { FilePicker, PortfolioList } from '@app/components';

interface Props {
  company: Company;
  updateCompany?: (company: Partial<Company>) => void;
  editable?: boolean;
  showHeader?: boolean;
}

const NOT_SPECIFIED = 'Не указан';

function ProfileCompany(props: Props) {
  const { company, updateCompany, editable = false, showHeader = true } = props;
  const { showNotification } = useNotification();
  const {
    user: authUser,
    company: authCompany,
    currentProfile,
    showSignInModal,
  } = useUserContext();
  const navigate = useNavigate();
  const [addEmployeeModalOpen, setAddEmployeeModalOpen] =
    useState<boolean>(false);
  const [reportModalVisible, setReportModalVisible] = useState<boolean>(false);

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);
  const onClickEdit = useCallback(() => {
    navigate(`/profile/company/${company!.bin}/edit`);
  }, [company, navigate]);
  const onShowAddEmployeeModal = useCallback(() => {
    setAddEmployeeModalOpen(true);
  }, []);
  const onCloseAddEmployeeModal = useCallback(() => {
    setAddEmployeeModalOpen(false);
  }, []);

  const onChangeFiles = useCallback(
    async (files: File[]) => {
      try {
        const response = await uploadCompanyFiles(company!.id, files);
        if (updateCompany) {
          updateCompany({ ...company, filesIds: response.filesIds });
        }
      } catch (e) {}
    },
    [updateCompany, company]
  );

  const onDeleteFile = useCallback(
    async (fileId: string) => {
      try {
        if (updateCompany) {
          updateCompany({
            filesIds: company
              .filesIds!.split(',')
              .filter((item) => item !== fileId)
              .join(','),
          });
        }
        await deleteCompanyFile(company.id, fileId);
        return true;
      } catch (e) {
        return false;
      }
    },
    [updateCompany, company]
  );

  const onClickShare = useCallback(async () => {
    try {
      if (!authUser.authenticated) {
        showSignInModal();
        return;
      }
      const url = `${window.location.hostname}/company/${company?.bin}`;
      await navigator.clipboard.writeText(url);
      showNotification({
        variant: 'success',
        message: 'Ссылка скопирована в буфер обмена',
      });
    } catch (e) {
      showNotification({
        variant: 'error',
        message: 'Ссылка не была скопирована',
      });
    }
  }, [authUser.authenticated, company?.bin, showNotification, showSignInModal]);

  const onClickReport = useCallback(() => {
    setReportModalVisible(true);
  }, []);
  const onCloseReportModal = useCallback(() => {
    setReportModalVisible(false);
  }, []);

  const actionButtons = useMemo<ReactNode[]>(() => {
    if (!company) return [];
    const returnActionButtons = [
      <Button
        key="share"
        disabled={currentProfile.blocked}
        onClick={onClickShare}
        variant="outline"
        size="sm"
        className="flex-1 sm:flex-none share-btn"
      >
        Поделиться
      </Button>,
    ];
    if (currentProfile.id !== company.id) {
      returnActionButtons.push(
        <Button
          key="report"
          variant="destructive"
          size="sm"
          onClick={onClickReport}
          disabled={currentProfile.blocked}
          className="flex-1 sm:flex-none"
        >
          Пожаловаться
        </Button>
      );
    }
    returnActionButtons.push(
      <Button
        key="email"
        variant="outline"
        size="icon"
        asChild
        className="shrink-0"
      >
        <a href={`mailto:${company.email}`}>
          <IconEmail16 />
        </a>
      </Button>
    );
    if (!!company.whatsAppNumber) {
      returnActionButtons.push(
        <Button
          key="wa"
          variant="outline"
          size="icon"
          asChild
          className="shrink-0"
        >
          <a
            href={`https://api.whatsapp.com/send?phone=${company.whatsAppNumber}`}
          >
            <IconWhatsApp16 />
          </a>
        </Button>
      );
    }
    if (company.phones) {
      returnActionButtons.push(
        <Button
          key="phone"
          variant="outline"
          size="icon"
          asChild
          className="shrink-0"
        >
          <a href={`tel:+${company?.phones[0]}`}>
            <IconPhone16 />
          </a>
        </Button>
      );
    }
    return [...returnActionButtons];
  }, [
    company,
    currentProfile.blocked,
    currentProfile.id,
    onClickShare,
    onClickReport,
  ]);

  const leftContent = useMemo(
    () =>
      company && (
        <div className="flex flex-col items-center gap-4 w-full">
          <ProfileAvatar
            avatarFiles={
              !!company.avatarFilesIds
                ? company.avatarFilesIds.split(',')[0]
                : ''
            }
            editable={false}
            className="mx-auto"
          />
          <AboutUser
            ratingUser={company.rating}
            views={14}
            lastDateUpdate={9}
            email={company.email}
            instagram={company.instagram || NOT_SPECIFIED}
            telegram={company.telegram || NOT_SPECIFIED}
            phones={company.phones}
            whatAppNumber={company.whatsAppNumber}
          />
        </div>
      ),
    [company]
  );

  const rightContent = useMemo(
    () => company && <ProfileCompanyInfo company={company} />,
    [company]
  );

  const rightControls = useMemo<ReactNode[]>(() => {
    if (authUser.id === company?.owner.id)
      return [
        <Button
          key="edit"
          onClick={onClickEdit}
          disabled={company.blocked}
          variant="outline"
          size="sm"
          className="action-link-fade shrink-0"
        >
          <IconEdit16 className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">Редактировать</span>
          <span className="sm:hidden sr-only">Ред</span>
        </Button>,
      ];
    return [];
  }, [authUser.id, company.blocked, company?.owner.id, onClickEdit]);

  const leftInfo = useMemo<ReactNode[]>(() => {
    if (company.blocked)
      return [
        <div
          key="blocked"
          className="bg-destructive text-destructive-foreground rounded-md px-3 py-1 text-sm font-medium"
        >
          Компания заблокирована
        </div>,
      ];
    return [];
  }, [company.blocked]);

  return (
    <div className="w-full pb-6 sm:pb-8 lg:pb-10">
      <LayoutProfile
        pending={false}
        toolbarProps={
          showHeader
            ? {
                onClickBack,
                title: 'Данные организации',
                rightControls: editable ? rightControls : [],
                leftInfo,
              }
            : null
        }
        leftContent={leftContent}
        rightContent={rightContent}
      >
        <Card className="w-full">
          <CardContent className="p-4 sm:p-6">
            <Tabs defaultValue="0" className="w-full">
              <TabsList
                className={`flex flex-col md:grid w-full gap-1 h-auto p-1 ${
                  editable ? 'md:grid-cols-5' : 'md:grid-cols-3'
                }`}
              >
                <TabsTrigger
                  value="0"
                  className="text-xs sm:text-sm px-3 py-3 sm:px-4 whitespace-normal h-auto w-full justify-start md:justify-center"
                >
                  <span className="hidden lg:inline">
                    Профессиональные данные
                  </span>
                  <span className="lg:hidden">Проф. данные</span>
                </TabsTrigger>

                {editable && (
                  <TabsTrigger
                    value="1"
                    className="text-xs sm:text-sm px-3 py-3 sm:px-4 whitespace-normal h-auto w-full justify-start md:justify-center"
                  >
                    Сотрудники
                  </TabsTrigger>
                )}

                {editable && (
                  <TabsTrigger
                    value="2"
                    className="text-xs sm:text-sm px-3 py-3 sm:px-4 whitespace-normal h-auto w-full justify-start md:justify-center"
                  >
                    <span className="hidden lg:inline">Документы и Файлы</span>
                    <span className="lg:hidden">Документы</span>
                  </TabsTrigger>
                )}

                <TabsTrigger
                  value="3"
                  className="text-xs sm:text-sm px-3 py-3 sm:px-4 whitespace-normal h-auto w-full justify-start md:justify-center"
                >
                  <span className="hidden lg:inline">Опыт работы</span>
                  <span className="lg:hidden">Опыт</span>
                </TabsTrigger>

                <TabsTrigger
                  value="4"
                  className="text-xs sm:text-sm px-3 py-3 sm:px-4 whitespace-normal h-auto w-full justify-start md:justify-center"
                >
                  Отзывы
                </TabsTrigger>
              </TabsList>

              <TabsContent value="0" className="mt-4 sm:mt-6">
                <div className="grid gap-4">
                  <ProfileCompanyAdditionalInfo company={company} />
                </div>
              </TabsContent>

              {editable && (
                <TabsContent value="1" className="mt-4 sm:mt-6">
                  <div className="w-full space-y-4">
                    <div className="flex justify-end">
                      <Button
                        onClick={onShowAddEmployeeModal}
                        disabled={company.blocked}
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                      >
                        <IconPlusCircle24 className="mr-2 h-4 w-4" />
                        Добавить сотрудника
                      </Button>
                    </div>
                    <UsersSearch
                      defaultParams={{ companyId: company.id, type: undefined }}
                      initialFilter={{ tenderType: TenderTypes.OFFER }}
                      isRenderFilter={false}
                      invertBgColorCard
                    />
                    <ModalAddEmployee
                      visible={addEmployeeModalOpen}
                      onClose={onCloseAddEmployeeModal}
                    />
                  </div>
                </TabsContent>
              )}

              {editable && (
                <TabsContent value="2" className="mt-4 sm:mt-6">
                  <FilePicker
                    loadableFiles={
                      !!company.filesIds ? company.filesIds.split(',') : []
                    }
                    onChange={onChangeFiles}
                    editable={editable && !company.blocked}
                    onDelete={onDeleteFile}
                  />
                </TabsContent>
              )}

              <TabsContent value="3" className="mt-4 sm:mt-6">
                <PortfolioList
                  companyId={company.id}
                  editable={editable && !company.blocked}
                />
              </TabsContent>

              <TabsContent value="4" className="mt-4 sm:mt-6">
                <Reviews
                  companyId={company.id}
                  showButton={
                    (!authCompany || company.id !== authCompany.id) &&
                    !company.blocked
                  }
                />
              </TabsContent>
            </Tabs>

            {!editable && authUser.authenticated && (
              <div className="mt-6 pt-4 border-t">
                <div className="flex sm:flex-row items-stretch sm:items-center justify-end gap-2 p-4 sm:p-6 pt-0">
                  {actionButtons.map((action, actionIndex) => (
                    <Fragment key={actionIndex}>{action}</Fragment>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </LayoutProfile>

      {company && (
        <ModalReport
          company={company}
          visible={reportModalVisible}
          onClose={onCloseReportModal}
        />
      )}
    </div>
  );
}

export default memo(ProfileCompany);
